import PropTypes from "prop-types";
import { isValidEmailAddress } from "../../utilities/validators";
import {
  setEmailAddress,
  setFirstName,
  setLastName,
  setOrderNumber,
  setReason,
} from "../../state/cancellation-form-details/reducer";
import { useEffect, useState } from "react";
import { Button } from "@asosteam/asos-web-component-library-button";
import { TextField } from "../../components/text-field";
import styles from "./styles";
import { useCancellationFormDetails } from "../../context/cancellation-details";
import { TextArea } from "../../components/text-area";
import { useLocaleProvider, useTranslate } from "../../context/locale-provider";
import { postToEventGrid } from "../../utilities/post-to-event-grid";

export default function CancellationForm({ setFormStatus }) {
  const [submitInProgress, setSubmitInProgress] = useState(false);
  const [hasErrors, setHasErrors] = useState(false);

  const translate = useTranslate(useLocaleProvider());
  const [state, dispatch] = useCancellationFormDetails();
  const { firstName, lastName, orderNumber, email, reason } =
    state.cancellationFormDetails;

  const [validationErrors, setValidationErrors] = useState({
    firstName: false,
    lastName: false,
    orderNumber: false,
    email: false,
    reason: false,
  });

  useEffect(() => {
    setHasErrors(Object.values(validationErrors).find((item) => item));
  }, [validationErrors]);

  const handleSubmit = async () => {
    const validationErrorState = {
      firstName: !firstName,
      lastName: !lastName,
      orderNumber: !orderNumber,
      email: !isValidEmailAddress(email),
      reason: !reason,
    };

    setValidationErrors(validationErrorState);
    const errorsPresent = Object.values(validationErrorState).find(
      (item) => item
    );
    setHasErrors(errorsPresent);
    if (!errorsPresent) {
      const locale =
        window.asos.webContext && window.asos.webContext.getBrowseLanguage();
      const payload = {
        first_name: firstName,
        last_name: lastName,
        orderNumber,
        email,
        reason,
        locale,
      };
      try {
        const response = await postToEventGrid(payload);
        if (response.status === 200) {
          setFormStatus("success");
        } else {
          setFormStatus("error");
        }
      } catch (e) {
        setFormStatus("error");
      }
    }
  };

  const onSubmit = async () => {
    setSubmitInProgress(true);
    await handleSubmit();
    setSubmitInProgress(false);
  };

  return (
    <div className={styles.container}>
      <div className={styles.form}>
        <span className={`${styles.requiredLabel} app_leavesden2`}>
          {translate("premier_cancel_contracts_form_required_information")}
        </span>
        <div className={styles.formControl}>
          <label htmlFor="firstName" className="app_london3">
            {translate(
              "premier_cancel_contracts_form_first_question_first_name"
            )}
          </label>
          <TextField
            id="firstName"
            ariaLabel={translate(
              "premier_cancel_contracts_form_first_question_first_name"
            )}
            initialValue={firstName}
            onChange={(e) => {
              dispatch(setFirstName(e.target.value));
              setValidationErrors({
                ...validationErrors,
                firstName: !e.target.value,
              });
            }}
            hasError={validationErrors.firstName}
            errorMessage={translate(
              "premier_cancel_contracts_form_first_error_required_field"
            )}
            required
          />
        </div>
        <div className={styles.formControl}>
          <label htmlFor="lastName" className="app_london3">
            {translate(
              "premier_cancel_contracts_form_second_question_last_name"
            )}
          </label>
          <TextField
            id="lastName"
            ariaLabel={translate(
              "premier_cancel_contracts_form_second_question_last_name"
            )}
            initialValue={lastName}
            onChange={(e) => {
              dispatch(setLastName(e.target.value));
              setValidationErrors({
                ...validationErrors,
                lastName: !e.target.value,
              });
            }}
            hasError={validationErrors.lastName}
            errorMessage={translate(
              "premier_cancel_contracts_form_first_error_part_two_required_field"
            )}
            required
          />
        </div>
        <div className={styles.formControl}>
          <label htmlFor="orderNumber" className="app_london3">
            {translate(
              "premier_cancel_contracts_form_fifth_question_order_number"
            )}
          </label>
          <TextField
            id="orderNumber"
            ariaLabel={translate(
              "premier_cancel_contracts_form_fifth_question_order_number"
            )}
            initialValue={orderNumber}
            onChange={(e) => {
              dispatch(setOrderNumber(e.target.value));
              setValidationErrors({
                ...validationErrors,
                orderNumber: !e.target.value,
              });
            }}
            hasError={validationErrors.orderNumber}
            errorMessage={translate(
              "premier_cancel_contracts_form_fifth_error_required_field"
            )}
            required
            description={translate(
              "premier_cancel_contracts_form_fifth_question_description"
            )}
          />
        </div>
        <div className={styles.formControl}>
          <label htmlFor="emailAddress" className="app_london3">
            {translate("premier_cancel_contracts_form_second_question")}
          </label>
          <TextField
            id="emailAddress"
            ariaLabel={translate(
              "premier_cancel_contracts_form_second_question"
            )}
            initialValue={email}
            onChange={(e) => {
              dispatch(setEmailAddress(e.target.value));
              setValidationErrors({
                ...validationErrors,
                email: !isValidEmailAddress(e.target.value),
              });
            }}
            hasError={validationErrors.email}
            errorMessage={translate(
              "premier_cancel_contracts_form_second_error_required_field"
            )}
            required
            description={translate(
              "premier_cancel_contracts_form_second_question_description"
            )}
          />
        </div>
        <div className={styles.formControl}>
          <label htmlFor="reason" className="app_london3">
            {translate("premier_cancel_contracts_form_third_question")}
          </label>
          <div className={styles.formControlText}>
            <p className="app_leavesden2">
              {translate("premier_cancel_contracts_form_third_question_detail")}
            </p>
          </div>
          <div className={styles.formControlText}>
            <p className="app_leavesden2">
              {translate(
                "premier_cancel_contracts_form_third_question_extra_description"
              )}
            </p>
          </div>
          <TextArea
            id="reason"
            ariaLabel={translate(
              "premier_cancel_contracts_form_third_question"
            )}
            initialValue={reason}
            onChange={(e) => {
              dispatch(setReason(e.target.value));
              setValidationErrors({
                ...validationErrors,
                reason: !e.target.value,
              });
            }}
            hasError={validationErrors.reason}
            errorMessage={translate(
              "premier_cancel_contracts_form_third_error_required_field"
            )}
            required
            maxLength={4000}
          />
        </div>

        <Button
          className="app_button"
          onClick={onSubmit}
          disabled={submitInProgress || hasErrors}
          isInProgress={submitInProgress}
        >
          {translate("premier_cancel_contracts_form_cancel_now_cta")}
        </Button>
      </div>
    </div>
  );
}

CancellationForm.propTypes = {
  setFormStatus: PropTypes.func.isRequired,
};
